import classNames from 'classnames';
import { isNull } from 'lodash';
import React from 'react';
import { IMarkupLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

export const LabelStyle: React.FC<{
    id: string;
    onAfterChange?: Function;
    trackCode?: {
        bold?: string | number;
        italic?: string | number;
    };
}> = React.memo(({ id, onAfterChange, trackCode }) => {
    const { $t } = useLocaleStore();

    const res = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { label } = layer.data as IMarkupLayerProps;
        return {
            bold: label.bold,
            italic: label.italic,
        };
    });

    const committedStyleRef = React.useRef(res);
    const pendingStyleRef = React.useRef(res || { bold: false, italic: false });

    React.useEffect(() => {
        const committedStyle = committedStyleRef.current;
        if (res && (!committedStyle || committedStyle.bold !== res.bold || committedStyle.italic !== res.italic)) {
            committedStyleRef.current = res;
            pendingStyleRef.current = res;
        }
    }, [res]);

    if (isNull(res)) return null;

    const { bold, italic } = res;

    return (
        <div className="c-markup-panel__item">
            <LabelAction title={$t((v) => v.panel.markupPanel.style)} align={true}>
                <div className="c-markup-label__style">
                    <div
                        className={classNames('c-markup-label__style-item', {
                            active: bold,
                        })}
                        onClick={() => {
                            const nextStyle = {
                                ...pendingStyleRef.current,
                                bold: !pendingStyleRef.current.bold,
                            };
                            pendingStyleRef.current = nextStyle;
                            const layer = editor.services.flatLayer.getLayer(id, true);
                            editor.markupLayer.setAttrOperateOriginData(layer);
                            editor.markupLayer.setLabelStyle(id, nextStyle.bold, nextStyle.italic);
                            editor.markupLayer.clearAttrOperateOriginData();
                            onAfterChange?.(trackCode?.bold);
                        }}
                    >
                        <IconFont name="TextBoldBold" fontSize={14} />
                    </div>
                    <div
                        className={classNames('c-markup-label__style-item', {
                            active: italic,
                        })}
                        onClick={() => {
                            const nextStyle = {
                                ...pendingStyleRef.current,
                                italic: !pendingStyleRef.current.italic,
                            };
                            pendingStyleRef.current = nextStyle;
                            const layer = editor.services.flatLayer.getLayer(id, true);
                            editor.markupLayer.setAttrOperateOriginData(layer);
                            editor.markupLayer.setLabelStyle(id, nextStyle.bold, nextStyle.italic);
                            editor.markupLayer.clearAttrOperateOriginData();
                            onAfterChange?.(trackCode?.italic);
                        }}
                    >
                        <IconFont name="TextItalicBold" fontSize={14} />
                    </div>
                </div>
            </LabelAction>
        </div>
    );
});
